import React, { useMemo } from "react";
import { Spin, Tooltip } from "antd";
import dayjs from "dayjs";
import { ExecutionCalendarDay, ExecutionCalendarMonth } from "../../../../api/SiteDaily";
import { LEGEND, WEEKDAYS, advanceText, monthCells, stateLabel } from "./calendarModel";
import "./calendar.css";

// The month grid of Daily Execution: each day's state, what was entered, and
// hollow chips for sections with nothing. Phones get a day list instead.
// No money on a cell — site users see quantities on this page.

const Chips: React.FC<{ day: ExecutionCalendarDay }> = ({ day }) => (
  <span className="pms-cal-chips" aria-hidden="true">
    <span className={`pms-cal-chip ${day.hasCivil ? "on" : ""}`}>C{day.hasCivil ? ` ${day.faceCount}` : ""}</span>
    <span className={`pms-cal-chip ${day.hasMaterial ? "on" : ""}`}>M{day.hasMaterial ? ` ${day.materialLineCount}` : ""}</span>
    <span className={`pms-cal-chip ${day.hasMechanical ? "on" : ""}`}>Mc{day.hasMechanical ? ` ${day.machineCount}` : ""}</span>
  </span>
);

const dayDescription = (day: ExecutionCalendarDay) => {
  const parts = [dayjs(day.date).format("dddd D MMMM"), stateLabel(day) || ""];
  if (day.holidayName) parts.push(day.holidayName);
  if (day.hasCivil) parts.push(`${day.faceCount} face${day.faceCount === 1 ? "" : "s"}`);
  if (day.advance.length) parts.push(advanceText(day.advance, 5));
  if (day.hasMaterial) parts.push(`${day.materialLineCount} material issue${day.materialLineCount === 1 ? "" : "s"}`);
  if (day.hasMechanical) parts.push(`${day.machineCount} machine${day.machineCount === 1 ? "" : "s"}`);
  return parts.filter(Boolean).join(", ");
};

const ExecutionCalendar: React.FC<{
  month: string;
  data: ExecutionCalendarMonth | null;
  loading: boolean;
  focusState?: string | null;
  onPick: (date: string) => void;
}> = ({ month, data, loading, focusState, onPick }) => {
  const cells = useMemo(() => monthCells(month), [month]);
  const byDate = useMemo(() => new Map((data?.days ?? []).map((day) => [day.date, day])), [data]);
  const listDays = (data?.days ?? []).filter((day) => day.state !== "future" && day.state !== "beforeStart");

  const cellBody = (day: ExecutionCalendarDay) => (
    <>
      <span className="pms-cal-num">
        {dayjs(day.date).date()}
        {stateLabel(day) && <span className="pms-cal-state">{stateLabel(day)}</span>}
      </span>
      {day.holidayName && <span className="pms-cal-line">{day.holidayName}</span>}
      {day.hasCivil && (
        <span className="pms-cal-line pms-cal-mono">
          {day.advance.length ? advanceText(day.advance) : `${day.faceCount} face${day.faceCount === 1 ? "" : "s"}`}
        </span>
      )}
      {day.boqLineCount > 0 && <span className="pms-cal-line pms-cal-sub">{day.boqLineCount} BOQ lines</span>}
      {day.openNoteCount > 0 && <span className="pms-cal-line pms-cal-sub">{day.openNoteCount} open note{day.openNoteCount === 1 ? "" : "s"}</span>}
      {day.state !== "future" && day.state !== "beforeStart" && <Chips day={day} />}
    </>
  );

  return (
    <div className="pms-cal">
      {loading && (
        <div className="pms-cal-loading">
          <Spin />
        </div>
      )}
      <div className="pms-cal-grid-wrap">
        <div className="pms-cal-grid" role="grid" aria-label={`Execution calendar ${dayjs(`${month}-01`).format("MMMM YYYY")}`}>
          {WEEKDAYS.map((weekday) => (
            <div key={weekday} className="pms-cal-dow" role="columnheader">
              {weekday}
            </div>
          ))}
          {cells.map((cell) => {
            const day = byDate.get(cell.date);
            if (!cell.inMonth || !day) {
              return (
                <div key={cell.date} className="pms-cal-day pms-cal-out" role="gridcell" aria-hidden="true">
                  <span className="pms-cal-num">{dayjs(cell.date).format("D MMM")}</span>
                </div>
              );
            }
            const dimmed = focusState && day.state !== focusState;
            return (
              <Tooltip key={cell.date} title={day.hasMaterial || day.hasMechanical ? `Material ${day.materialLineCount} · Machines ${day.machineCount}${day.machineHours ? ` (${day.machineHours} h)` : ""}` : ""}>
                <button
                  type="button"
                  role="gridcell"
                  className={`pms-cal-day pms-cal-${day.state} ${day.isToday ? "pms-cal-is-today" : ""} ${dimmed ? "pms-cal-dim" : ""}`}
                  onClick={() => onPick(day.date)}
                  aria-label={dayDescription(day)}
                >
                  {cellBody(day)}
                </button>
              </Tooltip>
            );
          })}
        </div>
      </div>

      <ul className="pms-cal-list" aria-label="Days of the month">
        {listDays.map((day) => (
          <li key={day.date}>
            <button type="button" className={`pms-cal-list-item pms-cal-${day.state}`} onClick={() => onPick(day.date)}>
              <span className="pms-cal-list-date">{dayjs(day.date).format("ddd D MMM")}</span>
              <span className="pms-cal-state">{stateLabel(day)}</span>
              <span className="pms-cal-list-text">
                {day.hasCivil ? advanceText(day.advance, 3) || `${day.faceCount} face(s)` : day.holidayName || ""}
              </span>
              <Chips day={day} />
            </button>
          </li>
        ))}
      </ul>

      <div className="pms-cal-legend">
        {LEGEND.map((item) => (
          <span key={item.state}>
            <i className={`pms-cal-swatch pms-cal-${item.state}`} />
            {item.label}
          </span>
        ))}
        <span>C · M · Mc = Civil · Material-Stores · Mechanical</span>
      </div>
    </div>
  );
};

export default ExecutionCalendar;
